import { Temporal } from '@js-temporal/polyfill';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';

import type { SupplyModel } from '../api/agreements.ts';
import { invoiceState, jobInvoiceQuery } from '../api/invoices.ts';
import {
  allocateUnit,
  bookOutLine,
  cancelJob,
  checkInLine,
  handOnLine,
  jobQuery,
  jobsQuery,
  releaseUnit,
  reserveJobLine,
  unitOptionsQuery,
  updateJob,
  type Job,
} from '../api/jobs.ts';
import { meQuery } from '../api/me.ts';
import { venuesQuery } from '../api/venues.ts';
import { dayLabel, localLabel, quoteQuery } from '../api/quotes.ts';
import { Button } from '../components/button.tsx';
import { renderJobDocument, uploadToJob } from '../api/documents.ts';
import { CrewSummary } from './crew-summary.tsx';
import { JobInsuranceGaps } from './insurance.tsx';
import { JobSafetyPanel } from './safety.tsx';
import { DocumentsPanel } from './documents-panel.tsx';
import { Field } from '../components/field.tsx';
import { PageHeader } from '../components/page-header.tsx';
import { FulfilmentPanel } from './fulfilment-panel.tsx';
import { AccountManagerField } from './account-manager-picker.tsx';
import { SupplyModelField } from './supply-model-field.tsx';
import { EndClientField, takesVenueAccount, VenueAccountTick } from './venue-billing.tsx';
import { VenueField } from './venue-field.tsx';
import { JobInstructions } from './instructions.tsx';
import { JobVenueWarnings } from './venue-warnings.tsx';
import {
  CheckInPanel,
  type CheckInInput,
  completeToGo,
  dispatchLabel,
  readyToGo,
  unchosen,
} from './dispatch.tsx';
import { ChosenSerials, SerialPicker } from './serial-picker.tsx';
import { HandOnPanel, type HandOnInput } from './hand-on-panel.tsx';
import { heldFrom } from '../held-from.ts';
import { byRoom, EventTimes, SiteDetailsPanel } from './produced-event.tsx';
import type { SiteDetailsInput } from '../api/quotes.ts';
import {
  shortAdvice,
  VenueEquipmentLabel,
  VenueIncludesWarning,
  venueShortSentence,
} from './venue-equipment.tsx';

// One job (CB-REQ JOB-01 as built at v1.946). What was agreed — client, dates, lines — is shown
// and not edited: a change to it is a revision of the quote. What firms up afterwards — delivery,
// the client's PO, notes — is edited here. A line accepted short stays pencilled until it is
// reserved, which must happen before the gear leaves (AVL-03). No money: a job's lines are unpriced.
// A serialised line's units are chosen here once it is reserved (INV-11 at v1.948); bulk goes out by
// quantity. Each line is booked out and checked back in here too (WHS-16 at v1.949).

const JOB_STATUS: Record<Job['status'], string> = {
  confirmed: 'Confirmed',
  out: 'Out',
  returned: 'Returned',
  cancelled: 'Cancelled',
};

export function JobScreen() {
  const { jobId } = useParams({ from: '/authenticated/jobs/$jobId' });
  const job = useQuery(jobQuery(jobId));
  const me = useQuery(meQuery);
  const queryClient = useQueryClient();
  const mayManage = me.data?.capabilities.includes('jobs.manage') ?? false;
  // JOB-01 at v1.986: on site may be chosen where the job's venue keeps our stock.
  const venues = useQuery(venuesQuery);
  const keepsStockHere = !!venues.data?.find((v) => v.id === job.data?.venue?.id)?.holdsStock;
  const mayAllocate = me.data?.capabilities.includes('jobs.allocate') ?? false;
  const mayDispatch = me.data?.capabilities.includes('jobs.dispatch') ?? false;
  const seesQuotes = me.data?.capabilities.includes('quotes.view') ?? false;
  const seesInvoices = me.data?.capabilities.includes('invoices.view') ?? false;
  const seesShifts = me.data?.capabilities.includes('shifts.view') ?? false;
  const mayManageShifts = me.data?.capabilities.includes('shifts.manage') ?? false;
  const today = me.data ? Temporal.Now.plainDateISO(me.data.tenant.defaultTz).toString() : '';

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: jobQuery(jobId).queryKey });
    await queryClient.invalidateQueries({ queryKey: jobsQuery.queryKey });
    // The last check-in drafts an account client's invoice (FIN-01), so the job's invoice is asked
    // for again too, rather than reading "None yet" until the page is reloaded.
    await queryClient.invalidateQueries({ queryKey: jobInvoiceQuery(jobId).queryKey });
  };
  const venue = useMutation({
    mutationFn: (venueId: string | null) => updateJob(jobId, { venueId }),
    onSuccess: refresh,
  });
  const supplyModel = useMutation({
    mutationFn: (model: SupplyModel) => updateJob(jobId, { supplyModel: model }),
    onSuccess: refresh,
  });
  const accountManager = useMutation({
    mutationFn: (accountManagerId: string | null) => updateJob(jobId, { accountManagerId }),
    onSuccess: refresh,
  });
  const fulfilment = useMutation({
    mutationFn: (input: Parameters<typeof updateJob>[1]['fulfilment']) =>
      updateJob(jobId, { fulfilment: input! }),
    onSuccess: refresh,
  });
  const site = useMutation({
    mutationFn: (input: SiteDetailsInput) => updateJob(jobId, { site: input }),
    onSuccess: refresh,
  });
  const endClient = useMutation({
    mutationFn: (name: string | null) => updateJob(jobId, { endClientName: name }),
    onSuccess: refresh,
  });
  // CMP-02 at v1.967: whether this job's shifts need SWMS.
  const swmsSwitch = useMutation({
    mutationFn: (swmsRequired: boolean) => updateJob(jobId, { swmsRequired }),
    onSuccess: refresh,
  });

  if (job.isPending) return <p className="text-muted">Loading…</p>;
  if (job.isError) {
    return (
      <p role="alert" className="alert">
        {job.error.message}
      </p>
    );
  }
  const j = job.data;
  const jobShort = shortAdvice(j.lines, j.shortLines);

  return (
    <div className="flex flex-col gap-6">
      <Link to="/jobs" className="link self-start text-sm">
        ← Jobs
      </Link>
      <PageHeader
        kicker={`Job · ${JOB_STATUS[j.status]}`}
        title={j.reference}
        meta={`${j.type === 'produced_event' ? 'Produced event · ' : ''}${j.name} · ${j.client.name}`}
      />

      {j.cancellation ? (
        <p className="notice">
          Cancelled {dayLabel(j.cancellation.at.slice(0, 10))}: {j.cancellation.reason}. Its holds
          are released, and nothing on it moves now.
        </p>
      ) : null}
      {j.shortLines > 0 && j.status !== 'cancelled' ? (
        <p role="alert" className="alert">
          {/* Short at acceptance, or after the venue changed to one without the stock (INV-A12). */}
          {jobShort.own > 0
            ? `${jobShort.own} ${jobShort.own === 1 ? 'line is' : 'lines are'} short of stock and only pencilled. Reserve ${jobShort.own === 1 ? 'it' : 'them'} once stock frees up, or arrange a cross-hire, before the gear leaves. `
            : ''}
          {venueShortSentence(jobShort.venue)}
        </p>
      ) : null}
      {/* CMP-06 at v1.966: flagged, never refused. */}
      {j.status !== 'cancelled' ? <JobInsuranceGaps jobId={j.id} /> : null}

      <dl
        className={`stat-grid grid-cols-1 ${seesInvoices || j.eventWindows ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'}`}
      >
        {/* JOB-02 at v1.958: a produced event's three windows; its scheduled window spans them. */}
        {j.eventWindows ? (
          <EventTimes windows={j.eventWindows} />
        ) : (
          <>
            <div className="stat">
              <dt className="kicker">Goes out</dt>
              <dd className="mt-1.5 font-extrabold">{localLabel(j.scheduledWindow.localStart)}</dd>
            </div>
            <div className="stat">
              <dt className="kicker">Comes back</dt>
              <dd className="mt-1.5 font-extrabold">{localLabel(j.scheduledWindow.localEnd)}</dd>
            </div>
          </>
        )}
        <div className="stat">
          <dt className="kicker">From quote</dt>
          <dd className="mt-1.5 font-extrabold">
            {/* QUO-04 at v1.950: the version in force, which is the origin until one is revised. */}
            {seesQuotes ? <OriginQuote quoteId={j.quoteId} /> : '—'}
          </dd>
        </div>
        {seesInvoices ? (
          <div className="stat">
            <dt className="kicker">Invoice</dt>
            <dd className="mt-1.5 font-extrabold">
              <JobInvoice jobId={j.id} today={today} />
            </dd>
          </div>
        ) : null}
      </dl>

      <FulfilmentPanel
        fulfilment={j.fulfilment}
        editable={mayManage && j.status !== 'cancelled'}
        saving={fulfilment.isPending}
        error={fulfilment.error}
        onSave={(input) => fulfilment.mutateAsync(input)}
        onSiteOffered={keepsStockHere}
      />

      {/* CLI-01 at v1.984: copied from the quote; attribution, so editable at any stage. */}
      <AccountManagerField
        current={j.accountManager ?? null}
        editable={mayManage}
        saving={accountManager.isPending}
        error={accountManager.error}
        onChange={(id) => accountManager.mutate(id)}
      />

      {/* VEN-01 at v1.951: copied from the quote, and the job's own to change. */}
      <VenueField
        venue={j.venue ?? null}
        editable={mayManage && j.status !== 'cancelled'}
        saving={venue.isPending}
        error={venue.error}
        onChange={(venueId) => venue.mutate(venueId)}
      />
      {/* VEN-11 at v1.988: copied from the quote; fixed once the job is first invoiced. */}
      <SupplyModelField
        current={j.supplyModel}
        editable={mayManage && j.status !== 'cancelled'}
        saving={supplyModel.isPending}
        error={supplyModel.error}
        onChange={(model) => supplyModel.mutate(model)}
      />
      {/* VEN-11 at v1.989: copied from the quote; named on the venue's invoice. */}
      <EndClientField
        value={j.endClientName ?? null}
        shown={j.supplyModel === 'resale'}
        editable={mayManage && j.status !== 'cancelled'}
        saving={endClient.isPending}
        error={endClient.error}
        onSave={(name) => endClient.mutate(name)}
      />
      {/* VEN-02 at v1.973: what the venue's access says about this job, and its dock booking. */}
      {j.status !== 'cancelled' ? <JobVenueWarnings jobId={j.id} mayManage={mayManage} /> : null}
      {/* VEN-09 at v1.974: the venue's standing instructions, this job's to change. */}
      <JobInstructions
        jobId={j.id}
        mayManage={mayManage && j.status !== 'cancelled'}
        rooms={[
          ...new Map(
            j.lines.flatMap((l) => (l.room ? [[l.room.id, l.room] as const] : [])),
          ).values(),
        ]}
      />

      {/* JOB-01 at v1.958: copied from the quote, and the job's own to change. */}
      {j.site ? (
        <SiteDetailsPanel
          site={j.site}
          editable={mayManage && j.status !== 'cancelled'}
          saving={site.isPending}
          error={site.error}
          onSave={(input) => site.mutateAsync(input)}
        />
      ) : null}

      <section className="flex flex-col gap-4" aria-labelledby="lines">
        <h2 id="lines" className="section-title">
          Lines
        </h2>
        <LinesTable
          job={j}
          mayManage={mayManage}
          mayAllocate={mayAllocate}
          mayDispatch={mayDispatch}
          mayTickVenueAccount={
            mayManage && seesQuotes && takesVenueAccount(j.supplyModel) && j.status !== 'cancelled'
          }
          seesQuotes={seesQuotes}
          onChanged={refresh}
        />
      </section>

      {/* OPS-03 at v1.998: the job's crew in summary; its tasks and shifts are on the roster. */}
      {seesShifts ? <CrewSummary job={j} mayManage={mayManageShifts} mayLead={mayManage} /> : null}

      {/* CMP-02 / CMP-04 at v1.967: flagged, never refused. */}
      {seesShifts && j.status !== 'cancelled' ? (
        <JobSafetyPanel
          jobId={j.id}
          swmsRequired={j.swmsRequired}
          onSwitch={(on) => swmsSwitch.mutate(on)}
          switching={swmsSwitch.isPending}
          mayManageJob={mayManage}
        />
      ) : null}

      {/* DOC-01 and DOC-03 at v1.959: no prices on either; the docket is signed on paper. */}
      {j.status !== 'cancelled' ? (
        <DocumentsPanel
          owner="job"
          id={j.id}
          actions={[
            { label: 'Pick list', prepare: () => renderJobDocument(j.id, 'pick_list') },
            // DOC-01 at v1.986: on site, nothing is delivered, so there is no docket.
            ...(j.fulfilment.kind === 'on_site'
              ? []
              : [{ label: 'Delivery docket', prepare: () => renderJobDocument(j.id, 'docket') }]),
          ]}
          onUpload={mayDispatch ? (file) => uploadToJob(j.id, file) : undefined}
        />
      ) : null}

      <Details job={j} editable={mayManage && j.status !== 'cancelled'} onChanged={refresh} />

      {mayManage && j.status !== 'cancelled' ? <CancelJob job={j} onChanged={refresh} /> : null}
    </div>
  );
}

/**
 * FIN-01 at v1.947: a prepay job's invoice exists from acceptance, and its paid state is what lets
 * the gear leave (WHS-09). An account job has none until check-in.
 */
function JobInvoice({ jobId, today }: { jobId: string; today: string }) {
  const invoice = useQuery(jobInvoiceQuery(jobId));
  if (!invoice.data)
    return <span className="text-muted">{invoice.isPending ? '…' : 'None yet'}</span>;
  return (
    <Link to="/invoices/$invoiceId" params={{ invoiceId: invoice.data.id }} className="link">
      {invoice.data.reference
        ? `${invoice.data.reference} · ${invoiceState(invoice.data, today)}`
        : 'Draft, to issue'}
    </Link>
  );
}

/** QUO-01 at v1.946: a hire carries two numbers, so each screen shows the other. */
function OriginQuote({ quoteId }: { quoteId: string }) {
  const quote = useQuery(quoteQuery(quoteId));
  return (
    <Link to="/quotes/$quoteId" params={{ quoteId }} className="link">
      {quote.data
        ? `${quote.data.reference}${quote.data.version > 1 ? ` v${quote.data.version}` : ''}`
        : 'Quote'}
    </Link>
  );
}

function LinesTable({
  job,
  mayManage,
  mayAllocate,
  mayDispatch,
  mayTickVenueAccount,
  seesQuotes,
  onChanged,
}: {
  job: Job;
  mayManage: boolean;
  mayAllocate: boolean;
  mayDispatch: boolean;
  /** VEN-11 at v1.989: its state is read from the quote, whose line it is. */
  mayTickVenueAccount: boolean;
  /** INV-A14 at v1.989: whether the venue's gear is included or sold is read from the quote too. */
  seesQuotes: boolean;
  onChanged: () => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const hasVenueGear = job.lines.some((l) => l.venueEquipment);
  const quote = useQuery({
    ...quoteQuery(job.quoteId),
    enabled: mayTickVenueAccount || (seesQuotes && hasVenueGear),
  });
  // VEN-11 at v1.989: once the job's invoice has issued, a line's tick is fixed (the database
  // refuses a change), so it reads as text rather than a tick that is always refused.
  const invoice = useQuery({ ...jobInvoiceQuery(job.id), enabled: mayTickVenueAccount });
  const tickFixed = !!invoice.data?.reference;
  const venueAccount = new Map((quote.data?.lines ?? []).map((l) => [l.id, l.forVenueAccount]));
  const venueGearCharged = new Map(
    (quote.data?.lines ?? []).map((l) => [l.id, l.venueEquipment?.charged]),
  );
  const [picking, setPicking] = useState<string | null>(null);
  const [checkingIn, setCheckingIn] = useState<string | null>(null);
  const [handingOn, setHandingOn] = useState<string | null>(null);
  // A refusal leaves the line pencilled and its figures may have moved, so re-read either way.
  const reserve = useMutation({
    mutationFn: (lineId: string) => reserveJobLine(job.id, lineId),
    onSettled: onChanged,
  });
  // INV-11 at v1.948. After a choice or a refusal the units on offer may have changed too.
  const refreshUnits = async (lineId: string) => {
    await onChanged();
    await queryClient.invalidateQueries({ queryKey: unitOptionsQuery(job.id, lineId).queryKey });
  };
  const choose = useMutation({
    mutationFn: ({ lineId, assetId }: { lineId: string; assetId: string }) =>
      allocateUnit(job.id, lineId, assetId),
    onSettled: (_data, _error, { lineId }) => refreshUnits(lineId),
  });
  const release = useMutation({
    mutationFn: ({ lineId, assetId }: { lineId: string; assetId: string }) =>
      releaseUnit(job.id, lineId, assetId),
    onSettled: (_data, _error, { lineId }) => refreshUnits(lineId),
  });
  // WHS-16 at v1.949: one line at a time, or every ready line in turn, stopping at the first refusal
  // — which is the database's WHS-09 block, said as it is.
  const bookOut = useMutation({
    mutationFn: async (lineIds: readonly string[]) => {
      for (const lineId of lineIds) await bookOutLine(job.id, lineId);
    },
    // The units on offer change too: a unit gone is no longer one to choose.
    onSettled: async (_data, _error, lineIds) => {
      await onChanged();
      for (const lineId of lineIds) {
        await queryClient.invalidateQueries({
          queryKey: unitOptionsQuery(job.id, lineId).queryKey,
        });
      }
    },
  });
  const checkIn = useMutation({
    mutationFn: ({ lineId, input }: { lineId: string; input: CheckInInput }) =>
      checkInLine(job.id, lineId, input),
    onSettled: onChanged,
  });
  // INV-A13 at v1.957: the next job changes too, so every job's view is asked for again.
  const handOn = useMutation({
    mutationFn: ({ lineId, input }: { lineId: string; input: HandOnInput }) =>
      handOnLine(job.id, lineId, input),
    onSuccess: () => setHandingOn(null),
    onSettled: async () => {
      await onChanged();
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
  // INV-A14 at v1.991: the venue's own gear goes out and comes back with the job, never by hand.
  const movesWithJob = (l: Job['lines'][number]) => l.venueEquipment?.here === true;
  const ready = job.lines.filter((l) => !movesWithJob(l) && completeToGo(l));
  // Once a line has nothing left to go the picker has nothing to do, so it closes (the operator's
  // finding at slice 3's checks, 2026-10-01: it stayed open after book-out).
  const pickingLine = job.lines.find((l) => l.id === picking && l.dispatch.toGo > 0) ?? null;
  const checkingInLine = job.lines.find((l) => l.id === checkingIn && l.dispatch.out > 0) ?? null;
  const handingOnLine = job.lines.find((l) => l.id === handingOn && l.dispatch.out > 0) ?? null;
  const actions = mayManage || mayAllocate || mayDispatch;
  const roomed = job.type === 'produced_event' && job.lines.some((l) => l.room);
  const groups = roomed ? byRoom(job.lines) : [{ room: null, lines: [...job.lines] }];

  return (
    <div className="flex flex-col gap-2">
      {mayDispatch && ready.length > 1 ? (
        <div>
          <Button
            onClick={() => bookOut.mutate(ready.map((l) => l.id))}
            disabled={bookOut.isPending}
          >
            Book out all ({ready.length})
          </Button>
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="data-table">
          <caption className="sr-only">What goes out on this job</caption>
          <thead>
            <tr>
              <th scope="col">Item</th>
              <th scope="col" className="text-right">
                Qty
              </th>
              <th scope="col">Stock</th>
              <th scope="col">Serials</th>
              <th scope="col">Where</th>
              {actions ? (
                <th scope="col" className="sr-only">
                  Actions
                </th>
              ) : null}
            </tr>
          </thead>
          {/* JOB-14 at v1.958: a produced event's lines under their rooms, those with none last. */}
          {groups.map((group) => (
            <tbody key={group.room?.id ?? 'none'}>
              {roomed ? (
                <tr>
                  <th scope="colgroup" colSpan={actions ? 6 : 5} className="kicker pt-4">
                    {group.room?.name ?? 'No room'}
                  </th>
                </tr>
              ) : null}
              {group.lines.map((l) => (
                <tr key={l.id}>
                  <th scope="row" className="font-semibold">
                    {l.description}
                    <VenueEquipmentLabel
                      venueEquipment={
                        l.venueEquipment && {
                          ...l.venueEquipment,
                          charged: venueGearCharged.get(l.quoteLineId),
                        }
                      }
                    />
                    <VenueIncludesWarning venueIncludes={l.venueIncludes} />
                    {mayTickVenueAccount && quote.data && !l.venueEquipment ? (
                      <VenueAccountTick
                        quoteId={job.quoteId}
                        jobId={job.id}
                        lineId={l.quoteLineId}
                        checked={venueAccount.get(l.quoteLineId) ?? false}
                        editable={!tickFixed}
                        onChanged={onChanged}
                      />
                    ) : null}
                  </th>
                  <td className="text-right tabular-nums">{l.quantity}</td>
                  <td>
                    {/* INV-A14: another venue's equipment holds nothing here; not a shortage. */}
                    {l.venueEquipment && !l.venueEquipment.here ? (
                      <span className="text-xs">Not held: it stays at its own venue</span>
                    ) : l.availability ? (
                      <div className="flex flex-col gap-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={l.availability.hold === 'firm' ? 'tag tag-accent' : 'tag'}
                          >
                            {l.availability.hold === 'pencil'
                              ? 'Pencilled only'
                              : l.dispatch.toGo === 0 && dispatchLabel(l.dispatch)
                                ? l.dispatch.out > 0
                                  ? 'Sent'
                                  : 'Back'
                                : 'Reserved'}
                          </span>
                          {l.availability.short > 0 ? (
                            <span className="text-sm font-semibold text-accent-2-700">
                              Short {l.availability.short}
                            </span>
                          ) : null}
                        </div>
                        {heldFrom(l.availability.from) ? (
                          <span className="text-xs">{heldFrom(l.availability.from)}</span>
                        ) : null}
                        <span className="text-xs text-muted">
                          {l.availability.onHand} on hand
                          {l.availability.firmElsewhere > 0
                            ? ` · ${l.availability.firmElsewhere} held firmly elsewhere`
                            : ''}
                        </span>
                      </div>
                    ) : (
                      // A cancelled job's holds are released, so a line with none is not a service.
                      <span className="text-sm text-muted">
                        {job.status === 'cancelled' ? 'Released' : 'Service'}
                      </span>
                    )}
                  </td>
                  <td>
                    {l.serialised ? (
                      <ChosenSerials
                        allocated={l.allocated}
                        quantity={l.quantity}
                        mayAllocate={mayAllocate}
                        pending={release.isPending}
                        onRelease={(assetId) => release.mutate({ lineId: l.id, assetId })}
                      />
                    ) : (
                      <span className="text-sm text-muted">
                        {l.availability ? 'By quantity' : '—'}
                      </span>
                    )}
                  </td>
                  <td className="text-sm">{dispatchLabel(l.dispatch) || '—'}</td>
                  {actions ? (
                    <td className="text-right">
                      {mayManage && l.availability?.hold === 'pencil' ? (
                        <Button
                          variant="quiet"
                          onClick={() => reserve.mutate(l.id)}
                          disabled={reserve.isPending}
                        >
                          Reserve
                        </Button>
                      ) : null}
                      {movesWithJob(l) ? (
                        <span className="text-sm text-muted">With the job</span>
                      ) : null}
                      {!movesWithJob(l) &&
                      mayAllocate &&
                      l.serialised &&
                      l.availability?.hold === 'firm' &&
                      unchosen(l) > 0 &&
                      picking !== l.id ? (
                        <Button variant="quiet" onClick={() => setPicking(l.id)}>
                          Select unit IDs…
                        </Button>
                      ) : null}
                      {!movesWithJob(l) && mayDispatch && readyToGo(l) ? (
                        <Button
                          variant="quiet"
                          onClick={() => bookOut.mutate([l.id])}
                          disabled={bookOut.isPending}
                        >
                          Book out
                        </Button>
                      ) : null}
                      {!movesWithJob(l) &&
                      mayDispatch &&
                      l.dispatch.out > 0 &&
                      checkingIn !== l.id ? (
                        <Button
                          variant="quiet"
                          onClick={() => {
                            setHandingOn(null);
                            setCheckingIn(l.id);
                          }}
                        >
                          Check in…
                        </Button>
                      ) : null}
                      {!movesWithJob(l) &&
                      mayDispatch &&
                      l.dispatch.out > 0 &&
                      handingOn !== l.id ? (
                        <Button
                          variant="quiet"
                          onClick={() => {
                            setCheckingIn(null);
                            setHandingOn(l.id);
                          }}
                        >
                          Hand on…
                        </Button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
      {/* Below the table, not in a row of it: the table scrolls sideways on a phone, and a panel
          inside it would scroll out of reach with it (CB-CONV §12). */}
      {pickingLine ? (
        <LinePicker
          jobId={job.id}
          lineId={pickingLine.id}
          gone={
            new Set(
              pickingLine.allocated.filter((u) => u.dispatch !== 'allocated').map((u) => u.assetId),
            )
          }
          remaining={pickingLine.quantity - pickingLine.allocated.length}
          pending={choose.isPending}
          error={choose.error ?? release.error}
          onChoose={(assetId) => choose.mutate({ lineId: pickingLine.id, assetId })}
          onClose={() => setPicking(null)}
        />
      ) : null}
      {checkingInLine ? (
        <CheckInPanel
          line={checkingInLine}
          pending={checkIn.isPending}
          error={checkIn.error}
          onSubmit={(input) => checkIn.mutate({ lineId: checkingInLine.id, input })}
          onClose={() => setCheckingIn(null)}
        />
      ) : null}
      {handingOnLine ? (
        <HandOnPanel
          jobId={job.id}
          line={handingOnLine}
          pending={handOn.isPending}
          error={handOn.error}
          onSubmit={(input) => handOn.mutate({ lineId: handingOnLine.id, input })}
          onClose={() => setHandingOn(null)}
        />
      ) : null}
      {bookOut.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {bookOut.error.message}
        </p>
      ) : null}
      {reserve.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {reserve.error.message}
        </p>
      ) : null}
      {release.error && picking === null ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {release.error.message}
        </p>
      ) : null}
    </div>
  );
}

/** INV-11 at v1.948: the units on offer for one line, loaded when the picker opens. */
function LinePicker({
  jobId,
  lineId,
  ...picker
}: {
  jobId: string;
  lineId: string;
  gone: ReadonlySet<string>;
  remaining: number;
  pending: boolean;
  error: Error | null;
  onChoose: (assetId: string) => void;
  onClose: () => void;
}) {
  const units = useQuery(unitOptionsQuery(jobId, lineId));
  if (units.isPending) return <p className="text-muted">Loading the units…</p>;
  if (units.isError) {
    return (
      <p role="alert" className="alert">
        {units.error.message}
      </p>
    );
  }
  return <SerialPicker options={units.data} {...picker} />;
}

function Details({
  job,
  editable,
  onChanged,
}: {
  job: Job;
  editable: boolean;
  onChanged: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(job.name);
  const [clientPo, setClientPo] = useState(job.clientPo ?? '');
  const [notes, setNotes] = useState(job.notes ?? '');
  const save = useMutation({
    mutationFn: () =>
      updateJob(job.id, {
        name: name.trim(),
        clientPo: clientPo.trim() || null,
        notes: notes.trim() || null,
      }),
    onSuccess: async () => {
      await onChanged();
      setEditing(false);
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (name.trim()) save.mutate();
  }

  return (
    <section className="flex flex-col gap-3" aria-labelledby="details">
      <div className="flex items-center justify-between gap-4">
        <h2 id="details" className="section-title flex-1">
          Details
        </h2>
        {editable && !editing ? (
          <Button variant="quiet" onClick={() => setEditing(true)}>
            Change…
          </Button>
        ) : null}
      </div>
      {editing ? (
        <form onSubmit={submit} className="flex flex-col gap-4 border-2 border-divider p-4">
          <Field
            label="Job name"
            required
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <Field
            label="Client PO"
            value={clientPo}
            onChange={(e) => setClientPo(e.currentTarget.value)}
          />
          <Field label="Notes" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />
          {save.error ? (
            <p role="alert" className="text-sm font-semibold text-accent-2-700">
              {save.error.message}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button type="button" variant="quiet" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
          <div>
            <dt className="kicker">Client PO</dt>
            <dd className="mt-1.5">{job.clientPo ?? '—'}</dd>
          </div>
          <div>
            <dt className="kicker">Notes</dt>
            <dd className="mt-1.5 whitespace-pre-wrap">{job.notes ?? '—'}</dd>
          </div>
        </dl>
      )}
    </section>
  );
}

/**
 * JOB-10 at v1.951: cancelled before anything goes out, with a reason; at v1.993 an issued invoice
 * no longer stops it — AVenue drafts a credit for all of it. Where it can no longer be done, the
 * screen says why in its place — never a button that asks for a reason and then refuses (the
 * operator's decision, 2026-09-30).
 */
function CancelJob({ job, onChanged }: { job: Job; onChanged: () => Promise<void> }) {
  const invoice = useQuery(jobInvoiceQuery(job.id));
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const panel = useRef<HTMLFormElement>(null);
  // It opens at the foot of the page; on a phone that is below the fold, so it is brought into view.
  useEffect(() => {
    if (open) panel.current?.scrollIntoView?.({ block: 'nearest' });
  }, [open]);
  const cancel = useMutation({
    mutationFn: () => cancelJob(job.id, reason.trim()),
    onSuccess: async () => {
      await onChanged();
      setOpen(false);
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (reason.trim()) cancel.mutate();
  }

  if (job.status === 'returned') {
    return <p className="text-sm text-ink-soft">{allHome(job)}</p>;
  }
  if (job.status !== 'confirmed') {
    return (
      <p className="text-sm text-ink-soft">
        Gear on this job has gone out, so it can’t be cancelled. Check it back in instead.
      </p>
    );
  }
  if (!open) {
    return (
      <div>
        <Button variant="quiet" onClick={() => setOpen(true)}>
          Cancel job…
        </Button>
      </div>
    );
  }
  return (
    <form
      ref={panel}
      onSubmit={submit}
      className="flex flex-col gap-3 rounded-md border-2 border-accent p-4"
    >
      <p className="text-sm">
        Cancelling releases every hold and chosen serial and sets a draft invoice aside.
        {invoice.data?.status === 'issued'
          ? ` ${invoice.data.reference} gets a credit for all of it, to issue from the invoice.`
          : ''}{' '}
        It cannot be undone. Cancellation fees are not built yet.
      </p>
      <Field
        label="Why is it cancelled?"
        required
        value={reason}
        onChange={(e) => setReason(e.currentTarget.value)}
      />
      {cancel.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {cancel.error.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={cancel.isPending}>
          {cancel.isPending ? 'Cancelling…' : 'Yes, cancel'}
        </Button>
        <Button type="button" variant="quiet" onClick={() => setOpen(false)}>
          Back
        </Button>
      </div>
    </form>
  );
}

/**
 * A returned job: nothing on it is still out. Gear handed on (INV-A13) went to another job instead
 * of coming home, so the sentence says where rather than calling it all back.
 */
function allHome(job: Job): string {
  const onward = [
    ...new Set(job.lines.flatMap((l) => l.dispatch.handedOn.map((h) => h.reference))),
  ].join(' and ');
  if (!onward) return 'Everything on this job is back.';
  const home = job.lines.some(
    (l) => l.dispatch.returned + l.dispatch.damaged + l.dispatch.missing > 0,
  );
  return home
    ? `Everything on this job is back, or handed on to ${onward}.`
    : `Nothing on this job is still out: it was all handed on to ${onward}.`;
}
